import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { getBackendUrl } from '../apiConfig';
import './Subscription.css';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isActiveSubscription = (subscription) =>
  subscription?.status === 'active' || subscription?.status === 'trialing';

async function readSubscriptionFromFirestore(uid) {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) return null;
  return userDoc.data().subscription || null;
}

/** Stripe webhook may lag behind checkout session `complete`; poll Firestore briefly. */
async function waitForActiveSubscription(uid, { maxAttempts = 12, delayMs = 1500 } = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const subscription = await readSubscriptionFromFirestore(uid);
    if (isActiveSubscription(subscription)) return true;
    if (attempt < maxAttempts - 1) await sleep(delayMs);
  }
  return false;
}

const Subscription = () => {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);

  const backendUrl = getBackendUrl();
  const stripePublishableKey =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_live_51R777ORvHo620Rnetj4lz9xHKTduxgXiu6trrge6XSh6KZSGhn1qvxwIhlHcN1FkECccKoqXZz5y4zcy8rWGa5DO00dkOctIMv';
  const stripePricingTableId =
    process.env.REACT_APP_STRIPE_PRICING_TABLE_ID || 'prctbl_1RdgrVRvHo620RneIwNnV8DQ';

  useEffect(() => {
    if (!user) {
      console.log('No user authenticated, redirecting to login');
      setError('You must be logged in to view subscription plans.');
      toast.error('Please log in to view subscription plans.');
      navigate('/login');
      return;
    }

    const checkSubscriptionStatus = async (options = {}) => {
      const { pollForWebhook = false } = options;
      try {
        console.log('Checking subscription status for user:', user.uid);
        if (pollForWebhook) {
          const ok = await waitForActiveSubscription(user.uid);
          if (ok) {
            setSubscribed(true);
            setError('');
            return true;
          }
          return false;
        }
        const subscription = await readSubscriptionFromFirestore(user.uid);
        console.log('Subscription data:', subscription);
        if (isActiveSubscription(subscription)) {
          setSubscribed(true);
          setError('');
          return true;
        }
        return false;
      } catch (err) {
        console.error('Error checking subscription:', err);
        setError('Failed to load subscription status. Please try again.');
        toast.error('Failed to load subscription status.');
        return false;
      }
    };

    const checkSessionStatus = async (sessionId) => {
      try {
        console.log('Checking session status:', sessionId);
        const response = await fetch(`${backendUrl}/check-session-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.error || `HTTP ${response.status}`);
        }
        const { status } = body;
        console.log('Session status:', status);
        if (status === 'complete') {
          const hasSubscription = await checkSubscriptionStatus({ pollForWebhook: true });
          if (hasSubscription) {
            toast.success('Subscription successful! Redirecting to app...');
            navigate('/app');
          } else {
            setError('Subscription not yet activated. Please check back soon or contact support.');
            toast.warn('Subscription not yet activated. Please wait or contact support.');
          }
        } else if (status === 'open' || status === 'expired') {
          setError('Subscription canceled. Please choose a plan to continue.');
          toast.warn('Subscription canceled. Please choose a plan.');
        } else {
          setError('Unknown session status. Please try again or contact support.');
          toast.error('Unknown session status.');
        }
      } catch (err) {
        console.error('Error checking session status:', err);
        setError('Failed to verify subscription session. Please try again.');
        toast.error('Failed to verify subscription session.');
      }
    };

    const loadSubscriptionStatus = async () => {
      try {
        console.log('Loading subscription status');
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        const success = params.get('success');
        const canceled = params.get('canceled');

        if (sessionId) {
          await checkSessionStatus(sessionId);
        }

        if (success === 'true') {
          console.log('Processing success redirect');
          const hasSubscription = await checkSubscriptionStatus({ pollForWebhook: true });
          if (hasSubscription) {
            toast.success('Subscription successful! Redirecting to app...');
            navigate('/app');
          } else {
            setError('Subscription not yet activated. Please check back soon or contact support.');
            toast.warn('Subscription not yet activated. Please wait or contact support.');
          }
        }

        if (canceled === 'true') {
          setError('Subscription canceled. Please choose a plan to continue.');
          toast.warn('Subscription canceled. Please choose a plan.');
        }

        const alreadyActive = await checkSubscriptionStatus({ pollForWebhook: false });
        if (alreadyActive) {
          setLoading(false);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/pricing-table.js';
        script.async = true;
        script.onload = () => {
          console.log('Stripe script loaded');
          if (customElements.get('stripe-pricing-table')) {
            setLoading(false);
          } else {
            setError('Stripe pricing table component not available.');
            toast.error('Stripe pricing table not available.');
            setLoading(false);
          }
        };
        script.onerror = () => {
          console.log('Stripe script failed to load');
          setError('Failed to load Stripe pricing table script.');
          toast.error('Failed to load pricing table script.');
          setLoading(false);
        };
        document.body.appendChild(script);
      } catch (err) {
        console.error('Error loading subscription:', err);
        setError('An unexpected error occurred while loading the subscription.');
        toast.error('An unexpected error occurred.');
        setLoading(false);
      }
    };

    loadSubscriptionStatus();

    return () => {
      console.log('Cleaning up Subscription component');
    };
  }, [user, navigate, location.search, backendUrl]);

  if (!user) {
    console.log('No user, rendering null');
    return null;
  }

  console.log('Rendering main content', { loading, error, subscribed });

  return (
    <div className="subscription-page">
      <div className="subscription-container">
        <h2 className="subscription-title">Choose Your Plan</h2>
        {loading ? (
          <div className="loading-spinner">
            <ClipLoader color="#3498db" size={50} />
            <p>Loading subscription plans...</p>
          </div>
        ) : error ? (
          <p className="subscription-error">
            {error} <a href="mailto:support@customspro.ai">Contact support</a>
          </p>
        ) : subscribed ? (
          <p className="subscription-success">
            You are subscribed! <a href="/settings">Manage your subscription</a>
          </p>
        ) : (
          <>
            <stripe-pricing-table
              pricing-table-id={stripePricingTableId}
              publishable-key={stripePublishableKey}
              client-reference-id={user.uid}
              locale="auto"
            ></stripe-pricing-table>
            <p className="manage-link">
              Already subscribed? <a href="/settings">Manage your subscription</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Subscription;
